import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import './MembersPage.scss';
// Import card components for displaying members
import BrotherCard from '../BrotherCard/BrotherCard';
import BoardCard from '../BrotherCard/BoardCard';
import Reveal from '../Reveal/Reveal';
import getGradYear from '../../utils/getGradYear';

// Full pledge-class progression, oldest to newest. Classes with no current
// members simply render nothing — this keeps the roster future-proof
// without needing to add a new section by hand every semester.
const PLEDGE_CLASS_ORDER = [
  'Founding', 'Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta', 'Eta',
  'Theta', 'Iota', 'Kappa', 'Lambda', 'Mu', 'Nu', 'Xi', 'Omicron', 'Pi',
  'Rho', 'Sigma', 'Tau', 'Upsilon', 'Phi', 'Chi', 'Psi', 'Omega',
];

// Elected board, in order of precedence.
const BOARD_POSITION_ORDER = [
  'Regent', 'Vice Regent', 'Recording Secretary', 'Treasurer',
  'Corresponding Secretary', 'Marshal',
];

// Appointed board, in order of precedence.
const APPOINTED_POSITION_ORDER = [
  'Recruitment Chair',
  'Brotherhood Chair',
  'Professional Development Chair',
  'Service Chair',
  'Diversity, Equity, and Inclusion Chair',
  'Engineering Project Manager',
  'Historian',
  'Media and Design Chair',
  'Risk Management Chair',
  'Web Development Chair',
];

// Guards against blank/placeholder records (e.g. a roster row with no name
// filled in yet) without ever touching the source data.
function hasIdentity(brother) {
  return Boolean(brother && brother.name && brother.name.trim().length > 0);
}

function lastNameKey(brother) {
  const parts = (brother.name || '').trim().split(/\s+/);
  return (parts.length > 1 ? parts[parts.length - 1] : parts[0] || '').toLowerCase();
}

function byLastName(a, b) {
  return lastNameKey(a).localeCompare(lastNameKey(b));
}

function byPositionOrder(order, key) {
  return (a, b) => order.indexOf(a[key]) - order.indexOf(b[key]);
}

// Caps the per-card reveal stagger so a long class list never takes more
// than ~360ms (6 steps * 60ms) to finish revealing.
const MAX_REVEAL_INDEX = 6;
function revealIndex(i) {
  return Math.min(i, MAX_REVEAL_INDEX);
}

// Turns a pledge-class name into its `?view=` query value, e.g. 'Theta' -> 'theta'.
function classViewKey(className) {
  return className.toLowerCase();
}

// Resolves the requested `?view=` value against the filters that actually
// exist for this roster. Anything unrecognized (missing, stale, mistyped)
// safely falls back to 'all' rather than rendering an empty page.
function resolveView(requestedView, availableKeys) {
  return availableKeys.includes(requestedView) ? requestedView : 'all';
}

// Component that displays all active members organized by their initiation class
// Also displays the executive board members at the top
function BrothersPage(props) {
  const brothers = (props.brothers || []).filter(hasIdentity);
  const location = useLocation();
  const history = useHistory();

  const board = brothers
    .filter((brother) => brother.Board === 'True')
    .sort(byPositionOrder(BOARD_POSITION_ORDER, 'Position'));

  const appointedBoard = brothers
    .filter((brother) => brother.AppointedBoard === 'True')
    .sort(byPositionOrder(APPOINTED_POSITION_ORDER, 'AppointedPosition'));

  const classSections = PLEDGE_CLASS_ORDER
    .map((className) => ({
      className,
      members: brothers.filter((brother) => brother.class === className).sort(byLastName),
    }))
    .filter((section) => section.members.length > 0);

  const filters = [
    { key: 'all', label: 'All members' },
    ...(board.length > 0 ? [{ key: 'eboard', label: 'Executive Board' }] : []),
    ...(appointedBoard.length > 0 ? [{ key: 'aboard', label: 'Appointed Board' }] : []),
    ...classSections.map(({ className }) => ({ key: classViewKey(className), label: className })),
  ];

  const requestedView = new URLSearchParams(location.search).get('view') || 'all';
  const activeView = resolveView(requestedView, filters.map((filter) => filter.key));

  function selectView(key) {
    const search = key === 'all' ? '' : `?view=${key}`;
    history.push({ pathname: location.pathname, search });
  }

  const showBoard = activeView === 'all' || activeView === 'eboard';
  const showAppointedBoard = activeView === 'all' || activeView === 'aboard';
  const visibleClassSections =
    activeView === 'all'
      ? classSections
      : classSections.filter(({ className }) => classViewKey(className) === activeView);

  return (
    <div className="MembersPage">
      {/* Hero section with background image and title */}
      <div className="members-hero">
        <div className="top-heading">
          <h1>Members</h1>
          <p className="hero-subtitle">Meet the members of Theta Tau</p>
        </div>
      </div>

      <div
        className="MembersFilterGroup"
        role="group"
        aria-label="Filter members"
      >
        {filters.map(({ key, label }) => (
          <button
            type="button"
            key={key}
            className="MembersFilterGroup-button"
            aria-pressed={activeView === key}
            onClick={() => selectView(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="ArchiveContainer">
        {showBoard && board.length > 0 && (
          <section className="ArchiveSection" aria-labelledby="board-heading">
            <h2 id="board-heading" className="ArchiveSection-title">Executive Board</h2>
            <div className="ArchiveGrid">
              {board.map((brother, i) => (
                <Reveal as="div" variant="rise" index={revealIndex(i)} key={brother.id}>
                  <BoardCard
                    position={brother.Position}
                    name={brother.name}
                    headshot={brother.headshot}
                  />
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {showAppointedBoard && appointedBoard.length > 0 && (
          <section className="ArchiveSection" aria-labelledby="appointed-board-heading">
            <h2 id="appointed-board-heading" className="ArchiveSection-title">Appointed Board</h2>
            <div className="ArchiveGrid">
              {appointedBoard.map((brother, i) => (
                <Reveal as="div" variant="rise" index={revealIndex(i)} key={brother.id}>
                  <BoardCard
                    position={brother.AppointedPosition}
                    name={brother.name}
                    headshot={brother.headshot}
                  />
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {visibleClassSections.map(({ className, members }) => (
          <section
            className="ArchiveSection"
            aria-labelledby={`${className}-class-heading`}
            key={className}
          >
            <h2 id={`${className}-class-heading`} className="ArchiveSection-title">
              {className} Class
            </h2>
            <div className="ArchiveGrid">
              {members.map((brother, i) => (
                <Reveal as="div" variant="rise" index={revealIndex(i)} key={brother.id}>
                  <BrotherCard
                    name={brother.name}
                    year={getGradYear(brother)}
                    major={brother.major && brother.major[1]}
                    headshot={brother.headshot}
                  />
                </Reveal>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default BrothersPage;
